import { Flex, Typography } from "@aircall/tractor";
import { formatInternationalNumber, User, UserName } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import {
  categorizeSpeakers,
  deductMainSpeakers,
} from "@/helpers/participants/participants.helpers";
import { SectionHeader } from "../SectionHeader";
import { SectionLayout } from "../SectionLayout";

import { ParticipantProps } from "./SectionParticipants.decl";

import { SpeakerAvatar } from "@/components/SpeakerAvatar";
import { formatInternational } from "@/helpers/phone_number/phone_number.helpers";
import { useCall } from "@/hooks/useCall/useCall";
import { VirtualAgent } from "../VirtualAgent/VirtualAgent";

function Participant({
  channel,
  text,
  isVirtualAgent,
  phoneNumber,
  fullName,
}: ParticipantProps) {
  if (isVirtualAgent) {
    return <VirtualAgent name={text ?? ""} />;
  }

  if (phoneNumber && fullName) {
    return (
      <User hasTooltip={false}>
        <SpeakerAvatar channel={channel} />
        <Flex flexDirection="column">
          <UserName>{fullName}</UserName>
          <Typography variant="supportingRegularS" color="text-secondary">
            {formatInternationalNumber(phoneNumber)}
          </Typography>
        </Flex>
      </User>
    );
  }

  return (
    <User hasTooltip={false}>
      <SpeakerAvatar channel={channel} />
      {text && <UserName>{text}</UserName>}
    </User>
  );
}

export function SectionParticipants() {
  const { t } = useTranslation();
  const { call } = useCall();

  if (!call || !call.from || !call.to || !call.speakers) {
    return null;
  }

  const { mainAgentId, mainExternalNumber } = deductMainSpeakers({
    type: call.type,
    from: call.from,
    to: call.to,
  });

  const { mainAgent, externalUser, otherAgents, otherUsers } =
    categorizeSpeakers(call.speakers, mainAgentId, mainExternalNumber);

  return (
    <SectionLayout data-test="participants-section">
      <SectionHeader>{t("call_context.participants")}</SectionHeader>
      <Flex gap="xxs" flexDirection="column">
        {mainAgent && (
          <Participant
            isVirtualAgent={mainAgent.isVirtualAgent}
            channel={mainAgent.channel}
            text={
              mainAgent.fullName ?? formatInternational(mainAgent.number ?? "")
            }
          />
        )}
        {externalUser && (
          <Participant
            isVirtualAgent={externalUser.isVirtualAgent}
            channel={externalUser.channel}
            phoneNumber={externalUser.number}
            fullName={externalUser.fullName}
            text={
              externalUser.fullName ??
              formatInternational(externalUser.number ?? "")
            }
          />
        )}
        {otherAgents.length > 0 &&
          otherAgents.map((agent) => (
            <Participant
              isVirtualAgent={agent.isVirtualAgent}
              key={agent.channel}
              channel={agent.channel}
              text={agent.fullName ?? formatInternational(agent.number ?? "")}
            />
          ))}
        {otherUsers.length > 0 &&
          otherUsers.map((user) => (
            <Participant
              isVirtualAgent={user.isVirtualAgent}
              key={user.channel}
              channel={user.channel}
              text={user.fullName ?? formatInternational(user.number ?? "")}
            />
          ))}
      </Flex>
    </SectionLayout>
  );
}
